import { observer } from 'mobx-react-lite';

import { SITEMAP_BLOG_LANGS } from '../../types/blogTypes';
import { Helmet } from 'react-helmet-async';
import { useBlogContext } from './BlogProvider';
import { useLocation } from 'react-router';
import { useCallback } from 'react';
import { useBlogTrans } from '../blogHooks/useBlogTrans';
import _ from 'lodash';

/* 언어별 메타 태그 예시
// canonical 은 반드시 넣어줘야 함
<link rel="canonical" href="https://retirein40s.com/ko" />

<link rel="alternate" hreflang="en" href="https://retirein40s.com/en" />
<link rel="alternate" hreflang="ko" href="https://retirein40s.com/ko" />
<link rel="alternate" hreflang="ja" href="https://retirein40s.com/ja" />
<link rel="alternate" hreflang="x-default" href="https://retirein40s.com/en" />
*/

/**
 * 블로그의 메타 태그 설정을 위한 컴포넌트
 * - 각 페이지 컴포넌트에서 추가한다
 */
export const BlogMetaHeader = observer(
    ({
        title,
        description,
        image,
        langAlt = true,
        jsonld,
    }: {
        /**
         * 페이지 제목. 없는 경우 블로그 제목만 표시
         */
        title?: string;
        /**
         * 페이지 설명.
         */
        description?: string;
        /**
         * 페이지 이미지.
         */
        image?: string;
        /**
         * 언어 링크를 표시할지 여부
         */
        langAlt?: boolean;
        /**
         * JSON-LD 데이터
         */
        jsonld?: any;
    }) => {
        const baseUrl = 'https://retirein40s.com';
        const { lang } = useBlogContext();
        const { trans } = useBlogTrans();
        const location = useLocation();

        if (!lang) {
            return null;
        }

        const pathname = location.pathname || '';

        const getAltPathname = useCallback(
            (altLang: string) => {
                const altPathname = pathname.replace(new RegExp(`^/${lang}`), `/${altLang}`);
                return `${baseUrl}${altPathname}`;
            },
            [lang, pathname],
        );

        const serviceTitle = trans('blogIndexTitle');

        // 태그 내에 들어갈 땐 공백을 정리해줌
        const refinedDescription = description ? description.replace(/\s+/g, ' ') : '';

        // 나중에 채운다
        const author = '';

        return (
            <Helmet>
                <title>{title ? `${title} | ${serviceTitle}` : serviceTitle}</title>

                {refinedDescription && <meta name="description" content={refinedDescription} />}

                {langAlt && (
                    <>
                        <link rel="canonical" href={`${baseUrl}${pathname}`} />
                        {SITEMAP_BLOG_LANGS.map((altLang) => (
                            <link
                                key={altLang}
                                rel="alternate"
                                hrefLang={altLang}
                                href={getAltPathname(altLang)}
                            />
                        ))}
                        <link rel="alternate" hrefLang="x-default" href={getAltPathname('en')} />
                    </>
                )}

                {title && <meta property="og:title" content={title} />}
                {refinedDescription && (
                    <meta property="og:description" content={refinedDescription} />
                )}
                {image && <meta property="og:image" content={image} />}

                <meta name="robots" content={'all'} />
                <meta name="apple-mobile-web-app-title" content={serviceTitle} />
                <meta name="application-name" key="application-name" content={serviceTitle} />
                {author && <meta name="author" key="author" content={author} />}

                <meta name="twitter:card" key="twitter:card" content={'summary'} />
                <meta name="twitter:url" key="twitter:url" content={`${baseUrl}${pathname}`} />
                <meta name="twitter:title" key="twitter:title" content={title} />
                <meta
                    name="twitter:description"
                    key="twitter:description"
                    content={refinedDescription}
                />
                {author && <meta name="twitter:creator" key="twitter:creator" content={author} />}
                {image && <meta name="twitter:image" key="twitter:image" content={image} />}

                <meta property="og:type" key="og:type" content="website" />
                <meta property="og:title" key="og:title" content={title} />
                <meta property="og:description" key="og:description" content={refinedDescription} />
                <meta property="og:site_name" key="og:site_name" content={serviceTitle} />
                <meta property="og:url" key="og:url" content={`${baseUrl}${pathname}`} />
                {image && <meta property="og:image" key="og:image" content={image} />}
            </Helmet>
        );
    },
);

export default BlogMetaHeader;
